<script>
	const { assetUrl } = require('utilities/urlUtilities');

	module.exports = {
		components: {
			'outbound-link': require('components/outbound-link.vue').default
		},
		data() {
			return {
				title: 'Privacy Policy'
			};
		},
		methods: {
			assetUrl(url) {
				return assetUrl(url);
			}
		}
	};
</script>
<style lang="scss">
	.privacy-policy {
		.image-wrapper {
			clear: both;
			margin-bottom: 1rem;

			img {
				width: 120px;
				margin-left: 1rem;
				float: right;
			}
		}
	}
</style>
<template>
	<main
		id="content"
		class="main privacy-policy"
		role="main">
		<header class="margin-top-medium">
			<div class="wrapper">
				<h1 class="main-header-title">Privacy Policy</h1>
			</div>
		</header>
		<div class="main-content wrapper legal-copy">
			<section>
				<h2><em>Last Updated</em>: June 03, 2024</h2>

				<div class="image-wrapper">
					<img
						:src="assetUrl('/assets/dist/images/awards/spp_signatory.png')"
						alt="Student Privacy Pledge Signatory" />

					<p>
						Welcome! Thank you for visiting the ReadWorks, Inc. website (“ReadWorks”, “we”, “our”, or “us”). This Privacy Policy applies to our entire Site,
						including this website (readworks.org) and all of our subdomains (the “Site”).
					</p>
				</div>

				<p>
					When you visit our Site, you agree to our Privacy Policy and <a href="/terms">Terms of Use</a>. In the case of any material changes to our
					policy, we will provide a banner notification on our website to inform all users of said changes. This notification will include an "accept"
					button on the banner. Once you have consented to these changes by clicking on the "accept" button, you can continue to use our Site. We encourage
					you to review this Privacy Policy now and each time you visit our Site. If you do not agree to any term in the Privacy Policy, you should not use
					the Site.
				</p>

				<p>
					We are strongly committed to protecting the privacy of everyone who visits our Site, including our educator users and their students. This
					Privacy Policy should help you understand what information we collect about you and/or your students when you visit and use the Site and how we
					use any information we may collect. Before we get into the details, we want to say first and foremost that we are fully committed to student data
					privacy as we help educators improve student reading achievement.
				</p>

				<p>
					If you have any questions, comments, or concerns about our Privacy Policy, please email us at
					<a
						href="mailto:privacy@readworks.org"
						target="_blank"
						>privacy@readworks.org</a
					>
					or write to us at P.O. Box 24673, Brooklyn, NY 11202.
				</p>
			</section>

			<section>
				<h2>1. Children’s Privacy</h2>
				<p>
					ReadWorks knows how important it is to protect children’s privacy. Our Site is intended for any U.S.-based adult educator or education-minded
					parent or guardian to use in concert with students of elementary school age or older. No student of any age is meant to use the ReadWorks Site
					without the invitation, direction, and guidance of an adult.
				</p>

				<p>
					When educators, parents, or guardians create accounts on behalf of their students, we keep whatever name is provided by that adult in our
					database. When a student account in ReadWorks is set up from a pre-existing Google account, we collect the name provided by such educator,
					parent, or guardian as it appears in that Google account and store it in our database accordingly. With respect to any student accounts, we do
					not collect or store any student email addresses, including when a student uses a Google account to log in to the Site.
				</p>

				<p>
					Collection or use of data is limited to product requirements. ReadWorks does not collect geolocation data, biometric or health data, or
					behavioral data. We collect student names to ensure that parents, guardians, and educators are able to keep track of which students are which
					while using our Site. We do not store student names long-term and endeavor to delete such data once the corresponding student account or the
					class containing such student account is deleted by the educator, parent or guardian who set up such account. Student use activity (e.g., when an
					assignment begins or ends and how questions are answered) is stored anonymously and retained only for educational purposes. ReadWorks will not
					retain student personal information for longer than necessary to deliver services or for school purposes. ReadWorks will delete any student data
					once operationally practicable. Users cannot interact with untrusted users, including strangers. ReadWorks’ profile information is not shared for
					social interactions.
				</p>

				<p>
					If you are the parent, guardian, or educator of a child who has used the Site without such permission, please contact us at
					<a
						href="mailto:privacy@readworks.org"
						target="_blank"
						>privacy@readworks.org</a
					>.
				</p>
			</section>

			<section>
				<h2>2. How We Handle Data</h2>
				<section>
					<h3>a. Information We Collect</h3>
					<p>When you visit and use our Site, we may collect information about you.</p>

					<ul>
						<li>
							<em>Information you provide to us</em>: In order to get access to certain information or materials that are provided through the Site, you may
							have to provide information to us. We collect and store this information in a way that allows us to connect it to you personally, including,
							for example, your name and email address. We refer to this information as personal information. Additionally, we may collect and retain a
							record of all communications we have with you.
						</li>

						<li>
							<em>Information we collect through technology</em>: We may also collect information about you through technology. For example, we may collect
							your IP address each time you click on a page during a visit to our Site. The Site may also use other technical methods to track and analyze
							website traffic patterns, such as how often our users visit different parts of the Site. These technical methods may involve the transmission
							of information either directly to us or to another party we have authorized to collect and process information on our behalf, such as Amazon
							Web Services. More information on how Amazon Web Services uses data can be found at
							<outbound-link
								href="https://aws.amazon.com/privacy/"
								data-outbound-link="AWS_PRIVACY"
								>https://aws.amazon.com/privacy/</outbound-link
							>. We may also use technical methods in emails that we send educator or parent Site users to figure out if users have opened those emails
							and/or clicked on links in those emails. We may collect the information from these technical methods in a form that is personally identifiable.
						</li>
					</ul>
				</section>

				<section>
					<h3>b. Use of Collected Information</h3>
					<p>
						We do not disclose nonpublic personal information about users of the Site to any nonaffiliated third parties, except as described here or in our
						Terms of Use.
					</p>

					<p>
						We may use or disclose certain personal information we collect about our users when they visit and browse our Site as part of our normal
						operations, including through our promotional email platform, ActiveCampaign. We make every effort to segregate student data from that of
						educators, parents, and guardian users and do not share such data with ActiveCampaign. More information on how ActiveCampaign uses data can be
						found at
						<outbound-link
							href="https://www.activecampaign.com/legal/privacy-policy"
							data-outbound-link="ACTIVE_CAMPAIGN_PRIVACY"
							>https://www.activecampaign.com/legal/privacy-policy</outbound-link
						>. We may also use or disclose certain personal information to respond to specific requests we get from you and through our use of service
						providers in connection with the Site. These third-party service providers may have access to and use your personal information, but only as
						needed to perform the functions we have asked them to perform. All third parties adhere to the same security and data retention principles as
						ReadWorks. ReadWorks only shares your personal information with third-party service providers that are consistent with our privacy policy.
					</p>

					<p>
						ReadWorks does not display any advertisements on our Site nor provide promotional sweepstakes or contests. There is no traditional, contextual,
						or behavioral advertising on the ReadWorks website. Users can opt out or unsubscribe from marketing communications by selecting the
						“Unsubscribe” button at the bottom of every email or emailing ReadWorks at help@readworks.org.
					</p>

					<p>
						When we believe we have to disclose something because it is required by law, regulation, legal process, subpoena, document, or governmental
						request, we will disclose personal information we collect from you. We may also do so to help enforce our Terms of Use, protect your safety or
						security, or protect the safety and security of tangible or intangible property that belongs to us, to you, or to third parties. We may transfer
						all user information (which may include personal information) to our acquirers or successors. Our acquirers and successors in the future will
						act in accordance with this privacy policy. Other than as part of such a transaction, we will not sell any of your personal information.
					</p>

					<p>
						Non-personal information is information that does not personally identify you, including anonymous information and aggregate data. We may use
						this information to understand better how our visitors use the Site, provide visitors with customized services and information, improve the
						Site, and for other similar purposes. We may combine this information with personal information. We may share this information with others and
						use this information in any manner permitted by law, but any disclosure that identifies you personally will be governed by the above paragraphs
						on personal information.
					</p>
				</section>
			</section>

			<section>
				<h2>3. Cookies</h2>
				<p>
					Our Site uses “cookies” to enable ongoing access to and use of the Site and to help us in ongoing Site maintenance. Cookies are small text files
					that may be placed on a browser when you visit a web site. For instance, when you return to the Site after logging in, cookies provide
					information to the Site, including personal information, so that the Site can remember who you are.
				</p>

				<p>
					We use cookies to capture anonymous data from the actions of our users to improve our Site experience and the way it performs. Our system is set
					up to not use cookies with our student users except in cases where it is requested by such student’s school district. In such cases, the data
					generated is managed by the school district’s privacy policy.
				</p>

				<p>
					We get statistical information from cookies on, among other things, how often users use our Site, the pages users visit, and how long each visit
					is, as well as information about a user’s computer, operating system, browser, language, and country. If you go to the settings on your Internet
					browser, you can choose to have your computer warn you each time a cookie is sent or you can choose to turn off all cookies. Check your browser’s
					HELP menu to learn about your options. If you decide to turn off cookies, you may not have access to many features that make the use of our Site
					smoother, and some of the services on the Site might not work properly. You can delete any cookies at any time by using the relevant option of
					your Internet browser or by deleting cookies on your hard drive. If you continue to use the Site with your browser set to accept cookies, we will
					assume that you accept receiving all cookies coming from our Site.
				</p>
			</section>

			<section>
				<h2>4. Google Analytics & Google API Services</h2>
				<p>
					We use Google Analytics to collect information about how our visitors use our Site. Google Analytics collects information such as how often users
					visit the Site, what pages users visit, and what other sites they visited before or after coming to the Site. Our Site is set up to not use
					Google Analytics to collect data about our student users. More information on how Google Analytics uses data can be found at
					<outbound-link
						href="https://www.google.com/policies/privacy/partners/"
						data-outbound-link="GOOGLE_PARTNER_PRIVACY"
						>https://www.google.com/policies/privacy/partners/</outbound-link
					>. We use the information we get from Google Analytics only to improve our Site. By using our Site with your Internet browser set to accept
					cookies, you agree to Google Analytics’ use of cookies on our Site.
				</p>
				<p>
					Our site’s use and transfer to any other app of information received from Google APIs will adhere to
					<outbound-link
						href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
						data-outbound-link="GOOGLE_PARTNER_API_PRIVACY"
						>Google API Services User Data Policy</outbound-link
					>, including the Limited Use requirements.
				</p>
			</section>

			<section>
				<h2>5. Third Party Websites</h2>
				<p>
					Our Site contains links to websites owned and operated by third parties. We provide these links for convenience, we do not have control over the
					content of these websites, and we do not take on any liability or responsibility for these sites. Our Privacy Policy does not concern the use of
					information collected on these third party sites. If you choose to visit these third party sites, you should review their privacy policies to
					make sure you understand and are comfortable with their practices regarding your personal information.
				</p>
			</section>

			<section>
				<h2>6. Protection of Information</h2>
				<p>
					We work hard to protect your personal information. We store and process student data in accordance with industry best practices. This includes
					encryption and appropriate administrative, physical, and technical safeguards including firewalls to secure Student Data from unauthorized
					access, disclosure, and use. We conduct periodic risk assessments and remediate any identified security vulnerabilities in a timely manner. We
					regularly perform system audits and work to ensure all of our software has the latest security-related patches and updates. We also limit
					employee access to user information. Unfortunately, there is no such thing as “perfect security” on the Internet, and third parties may
					unlawfully intercept or access transmissions or private communications. We will not be responsible or liable for any damages, losses, or causes
					of action in connection with the disclosure of your personal information.
				</p>
			</section>

			<section>
				<h2>7. Do Not Track</h2>
				<p>
					We may track users’ use of the Site over time to continually improve our service to our users and our overall Site. Our Site is set up to not
					track student users’ use of the Site over time. We do not track visitors of the Site over time and across third party websites to provide
					targeted advertising and therefore do not respond to Do Not Track (“DNT”) signals. However, some third-party sites do keep track of browsing
					activity when they provide content, which allows them to tailor what they present to you. If you are visiting these sites, your browser allows
					you to set the DNT signal so that third parties (particularly advertisers) know you do not want to be tracked. You can consult the help pages of
					your browser to learn how to set your preferences so that websites do not track you.
				</p>
			</section>

			<section>
				<h2>8. Information Requests</h2>
				<p>
					If you would like us to disclose to you or delete certain information about our collection and use of your personal information, you may ask us
					to do so. Once we receive your request and verify your identity, we will disclose such information to you or delete the information you have
					asked us to delete, unless there is a legal exception. For example, we might deny your deletion request if we have to keep the information in
					order for us or our providers to complete the transaction for which we collected the personal information, provide a service that you requested,
					or take actions we reasonably anticipate within the context of our ongoing relationship with you.
				</p>

				<p>
					To submit a request please email us at
					<a
						href="mailto:privacy@readworks.org"
						target="_blank"
						>privacy@readworks.org</a
					>.
				</p>

				<p>Your request must:</p>

				<ul>
					<li>
						Provide enough information to allow us to reasonably verify that you are the person about whom we collected personal information or an
						authorized representative of such person.
					</li>
					<li>Describe your request with enough detail to allow us to understand, evaluate, and respond to it.</li>
				</ul>

				<p>
					We cannot respond to your request or provide you with non-public personal information if we cannot verify your identity or authority to make the
					request and confirm that the requested personal information relates to you. We will use the information provided in a request solely to verify
					your identity or authority to make the request.
				</p>
			</section>

			<section>
				<h2>9. Shine the Light</h2>
				<p>
					California Civil Code Section 1798.83, known as the “Shine The Light” law, permits our Site users who are California residents to request and
					obtain from us a list of their personal information (if any) we disclosed to third parties for direct marketing purposes in the preceding
					calendar year and the names and addresses of those third parties. Requests may be made only once a year and are free of charge. We currently do
					not share any personal information with third parties for their direct marketing purposes.
				</p>
			</section>

			<section>
				<h2>10. International Use</h2>
				<p>
					This Site is intended for use by individuals in the United States. Despite the global nature of the Internet, ReadWorks makes no claims that the
					Site is appropriate or may be viewed or used outside the United States. Additionally, our databases are located in the United States, and the
					data we collect from users is stored in the United States. This Site is intended for use by individuals in the United States.
					<strong
						><em
							>By sending us your information, you consent to its transfer to and storage within the United States and its use in a manner consistent with
							this Privacy Policy.</em
						></strong
					>
				</p>
			</section>

			<section>
				<h2>11. Governing Law</h2>
				<p>
					The laws of the State of New York govern this Privacy Policy. Any dispute relating to this Privacy Policy or your use of the Site or the Content
					shall be resolved solely in the state or federal courts located in New York, New York.
				</p>
			</section>

			<section>
				<h2>12. Contact Us</h2>
				<p>
					If you have any questions or comments about this Privacy Policy, you can email us at
					<a
						href="mailto:privacy@readworks.org"
						target="_blank"
						>privacy@readworks.org</a
					>
					or write to us at P.O. Box 24673, Brooklyn, NY 11202.
				</p>

				<p>Link to <a href="/data-breach-plan">ReadWorks’ Data Breach Plan</a>.</p>
			</section>
		</div>
	</main>
</template>
